import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useOnlineStatus } from 'hooks/useOnlineStatus'
import type {
  ItineraryUpdate,
  EnrichmentCompleteEvent,
  HotelSearchResultsData,
  HotelSearchErrorData,
  ChatUiActionData,
} from 'tripPlanner/api/types'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import type { UserData } from '../types'
import { useChatCore } from '../hooks/useChatCore'
import { useItineraryUpdates } from '../hooks/useItineraryUpdates'
import { useTripPlannerEventBus, useEventSubscription } from './TripPlannerEventBus'
import { ChatActionsContext, type ChatActionsValue } from './ChatActionsContext'
import { ChatStreamStatusContext, type ChatStreamStatusValue } from './ChatStreamStatusContext'
import { ChatScrollContext, type ChatScrollValue } from './ChatScrollContext'
import { ChatFilesContext, type ChatFilesValue } from './ChatFilesContext'
import { ChatUiActionContext, type ChatUiActionValue } from './ChatUiActionContext'
import { ChatStreamMessagesContext, type ChatStreamMessagesValue } from './ChatStreamMessagesContext'
import { ItineraryContext, type ItineraryContextValue } from './ItineraryContext'

export interface TripPlannerChatProviderProps {
  chatId: number | undefined
  setChatIdForStreaming: (id: number | undefined) => void
  prompt: string | undefined
  isDevMode: boolean
  isLoggedIn: boolean
  orderIds?: Array<string>
  offerIds?: Array<string>
  refetchChatList: () => Promise<unknown>
  startChatTitleGeneration: (chatId: number, message: string) => void
  updateChatLastActivityTimestamp: (chatId: number, iso: string) => void
  userData?: UserData
  chatKey: string
  children: React.ReactNode
}

export function TripPlannerChatProvider({ children, ...props }: TripPlannerChatProviderProps) {
  const eventBus = useTripPlannerEventBus()
  const [files, setFiles] = useState<Array<File>>([])

  const isStreamingRef = useRef(false)

  const isOnline = useOnlineStatus()

  // ── Wire streaming callbacks to event bus dispatch ──

  const onItineraryUpdates = useCallback((chatId: number, updates: Array<ItineraryUpdate>) => {
    eventBus.dispatch({ type: 'itinerary_updates', chatId, updates })
  }, [eventBus])

  const onEnrichmentComplete = useCallback((chatId: number, event: EnrichmentCompleteEvent) => {
    eventBus.dispatch({ type: 'enrichment_complete', chatId, event })
  }, [eventBus])

  const onStreamComplete = useCallback((chatId: number) => {
    eventBus.dispatch({ type: 'stream_complete', chatId })
  }, [eventBus])

  const onHotelSearchResults = useCallback((data: HotelSearchResultsData, generation: number) => {
    eventBus.dispatch({ type: 'hotel_search_results', data, generation })
  }, [eventBus])

  const onHotelSearchError = useCallback((data: HotelSearchErrorData, generation: number) => {
    eventBus.dispatch({ type: 'hotel_search_error', data, generation })
  }, [eventBus])

  // Buffer UI actions in state so they survive the landing→chat transition.
  // MapContainer may not be mounted when the first ui_action arrives (its event
  // subscription isn't active yet). The context-based buffer lets MapContainer
  // pick up the pending action on mount.
  const [pendingUiAction, setPendingUiAction] = useState<ChatUiActionData | null>(null)

  const clearPendingUiAction = useCallback(() => {
    setPendingUiAction(null)
  }, [])

  const onUiAction = useCallback((data: ChatUiActionData) => {
    setPendingUiAction(data)
    eventBus.dispatch({ type: 'ui_action', data })
  }, [eventBus])

  // ── Itinerary domain ──

  const {
    itineraryData,
    enrichingStopIds,
    enrichingItemIds,
    scrollTarget,
    clearScrollTarget,
    handleItineraryUpdates,
    handleEnrichmentComplete: handleItineraryEnrichmentComplete,
    handleRefetchItinerary,
    refetchCurrentChatItinerary,
  } = useItineraryUpdates({ chatId: props.chatId, isStreamingRef })

  // Provider dispatches these events (via callbacks wired into useChatCore) and subscribes
  // to them here. This round-trip through the event bus is intentional — it allows future
  // containers (MapContainer, ItineraryContainer) to independently subscribe to the same events.
  useEventSubscription('itinerary_updates', (event) => {
    handleItineraryUpdates(event.chatId, event.updates)
  })
  useEventSubscription('enrichment_complete', (event) => {
    handleItineraryEnrichmentComplete(event.chatId, event.event)
  })
  useEventSubscription('stream_complete', (event) => {
    handleRefetchItinerary(event.chatId)
  })
  // ── Chat core ──

  const messagesWrapperRef = useRef<HTMLDivElement | null>(null)

  const {
    messages,
    filteredMessages,
    userFeedbackMap,
    setUserFeedbackMap,
    isChatError,
    chatError,
    loadingState,
    isStreaming,
    isStreamOpen,
    isAwaitingMessageEnd,
    submitMessage,
    regenerateResponse,
    stopStreaming,
    retryLastMessage,
    handleScroll,
    isAtBottom,
    hasContentOutOfView,
    enableFollowAndScrollToBottom,
    setFollowTail,
    dynamicSpacerHeight,
    resetConversation,
    resetStreamingState,
    streamError,
    isStalled,
    reasoningSummaries,
    toolCallInfo,
    latestGenerationRef,
  } = useChatCore({
    chatId: props.chatId,
    setChatId: props.setChatIdForStreaming,
    prompt: props.prompt,
    isDevMode: props.isDevMode,
    files,
    setFiles,
    isLoggedIn: props.isLoggedIn,
    orderIds: props.orderIds,
    offerIds: props.offerIds,
    refetchChatList: props.refetchChatList,
    startChatTitleGeneration: props.startChatTitleGeneration,
    updateChatLastActivityTimestamp: props.updateChatLastActivityTimestamp,
    onItineraryUpdates,
    onEnrichmentComplete,
    onStreamComplete,
    onHotelSearchResults,
    onHotelSearchError,
    onUiAction,
    messagesWrapperRef,
    userData: props.userData,
    chatKey: props.chatKey,
  })

  // Sync isStreamingRef after render but before paint — useLayoutEffect eliminates the
  // 1-tick gap that would allow refetchOnWindowFocus to fire during streaming.
  useLayoutEffect(() => {
    isStreamingRef.current = isStreaming
  }, [isStreaming])

  // Single reset handler — placed after useChatCore so latestGenerationRef is in scope
  // Note: enrichment state (enrichingStopIds/enrichingItemIds) is derived from itinerary data
  // and auto-resets when chatId changes, so no manual reset is needed.
  useEventSubscription('reset_chat_state', () => {
    clearScrollTarget()
    latestGenerationRef.current = 0
    setFiles([])
    setPendingUiAction(null)
  })

  // ── sendMessage wrapper (offerId enrichment — Rule 3) ──

  const sendMessage = useCallback(async(message: string, options?: SendMessageOptions) => {
    let enrichedMessage = message
    if (options?.offerId) {
      const offerTypeLine = options.offerType ? `\nOffer Type: ${options.offerType}` : ''
      const contextLabel = options.offerType === 'experience' ? 'LE Experience offer' : 'LE Hotel offer'
      enrichedMessage = `${message}\n<Context label="${contextLabel}">Offer ID: ${options.offerId}${offerTypeLine}</Context>`
    }
    return submitMessage(enrichedMessage)
  }, [submitMessage])

  // ── Memoized context values ──

  const actionsValue = useMemo<ChatActionsValue>(() => ({
    submitMessage,
    sendMessage,
    regenerateResponse,
    stopStreaming,
    resetConversation,
    resetStreamingState,
    setFollowTail,
    retryLastMessage,
  }), [submitMessage, sendMessage, regenerateResponse, stopStreaming, resetConversation, resetStreamingState, setFollowTail, retryLastMessage])

  const statusValue = useMemo<ChatStreamStatusValue>(() => ({
    chatId: props.chatId,
    chatKey: props.chatKey,
    isStreaming,
    isStreamOpen,
    isAwaitingMessageEnd,
    loadingState,
    streamError,
    isStalled,
    isOnline,
    reasoningSummaries,
    toolCallInfo,
  }), [props.chatId, props.chatKey, isStreaming, isStreamOpen, isAwaitingMessageEnd, loadingState, streamError, isStalled, isOnline, reasoningSummaries, toolCallInfo])

  const scrollValue = useMemo<ChatScrollValue>(() => ({
    handleScroll,
    isAtBottom,
    hasContentOutOfView,
    enableFollowAndScrollToBottom,
    dynamicSpacerHeight,
    messagesWrapperRef,
  }), [handleScroll, isAtBottom, hasContentOutOfView, enableFollowAndScrollToBottom, dynamicSpacerHeight, messagesWrapperRef])

  const filesValue = useMemo<ChatFilesValue>(() => ({
    files,
    setFiles,
  }), [files, setFiles])

  const uiActionValue = useMemo<ChatUiActionValue>(() => ({
    pendingUiAction,
    clearPendingUiAction,
  }), [pendingUiAction, clearPendingUiAction])

  const streamMessagesValue = useMemo<ChatStreamMessagesValue>(() => ({
    messages,
    filteredMessages,
    isChatError,
    chatError,
    userFeedbackMap,
    setUserFeedbackMap,
  }), [messages, filteredMessages, isChatError, chatError, userFeedbackMap, setUserFeedbackMap])

  const hasItineraryData = Boolean(itineraryData?.stops?.length)

  const itineraryValue = useMemo<ItineraryContextValue>(() => ({
    itineraryData,
    hasItineraryData,
    enrichingStopIds,
    enrichingItemIds,
    scrollTarget,
    clearScrollTarget,
    refetchCurrentChatItinerary,
  }), [
    itineraryData, hasItineraryData,
    enrichingStopIds, enrichingItemIds,
    scrollTarget, clearScrollTarget,
    refetchCurrentChatItinerary,
  ])

  return (
    <ChatActionsContext.Provider value={actionsValue}>
      <ChatStreamStatusContext.Provider value={statusValue}>
        <ChatScrollContext.Provider value={scrollValue}>
          <ChatFilesContext.Provider value={filesValue}>
            <ChatUiActionContext.Provider value={uiActionValue}>
              <ChatStreamMessagesContext.Provider value={streamMessagesValue}>
                <ItineraryContext.Provider value={itineraryValue}>
                  {children}
                </ItineraryContext.Provider>
              </ChatStreamMessagesContext.Provider>
            </ChatUiActionContext.Provider>
          </ChatFilesContext.Provider>
        </ChatScrollContext.Provider>
      </ChatStreamStatusContext.Provider>
    </ChatActionsContext.Provider>
  )
}
